import type {
  ManualTebraEntryCase,
  ManualTebraServiceLine,
} from "../services/reports.service";
import type { BillingConfiguration } from "../services/billing.service";

export interface ManualEntryIdentifiers {
  tebraEncounterId: string;
  tebraPatientId?: string;
  tebraPatientCaseId?: string;
}

const POSITIVE_NUMERIC_ID = /^[1-9]\d*$/;

/** Remote-write controls are available only after an explicit API-mode response. */
export function isBillingAutomationAllowed(
  configuration: BillingConfiguration | null | undefined
): boolean {
  return (
    configuration?.automationEnabled === true && configuration.mode === "api"
  );
}

export function validateManualEntryIdentifiers(
  values: ManualEntryIdentifiers
): Partial<Record<keyof ManualEntryIdentifiers, string>> {
  const errors: Partial<Record<keyof ManualEntryIdentifiers, string>> = {};
  const fields: Array<keyof ManualEntryIdentifiers> = [
    "tebraEncounterId",
    "tebraPatientId",
    "tebraPatientCaseId",
  ];

  fields.forEach((field) => {
    const value = values[field]?.trim();
    if (field === "tebraEncounterId" && !value) {
      errors[field] = "Tebra encounter ID is required.";
    } else if (value && !POSITIVE_NUMERIC_ID.test(value)) {
      errors[field] = "Enter a positive numeric Tebra ID.";
    }
  });

  return errors;
}

export function getManualTebraServiceLines(
  packet: ManualTebraEntryCase
): ManualTebraServiceLine[] {
  return packet.serviceLines ?? [];
}

export function totalManualTebraCharges(
  packet: ManualTebraEntryCase
): number | null {
  const lines = getManualTebraServiceLines(packet);
  if (lines.some((line) => line.totalCharge === null)) return null;
  return lines.reduce((total, line) => total + (line.totalCharge ?? 0), 0);
}

export function canConfirmManualTebraEntry(
  packet: ManualTebraEntryCase
): boolean {
  if (!packet.readiness.ready || packet.readiness.errors.length > 0)
    return false;
  return packet.billingFactsSource === "signed_snapshot";
}
